import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { useMirrorViewer } from "@/hooks/useMirrorViewer";

const CornerFrame = () => (
    <>
        <div className="absolute top-4 left-4 w-7 h-7 border-l-2 border-t-2 border-white/25" />
        <div className="absolute top-4 right-4 w-7 h-7 border-r-2 border-t-2 border-white/25" />
        <div className="absolute bottom-4 left-4 w-7 h-7 border-l-2 border-b-2 border-white/25" />
        <div className="absolute bottom-4 right-4 w-7 h-7 border-r-2 border-b-2 border-white/25" />
    </>
);

function obsMessage(token, isLegacyLink, status) {
    if (isLegacyLink) {
        return "This OBS link is outdated — log in to SPCLIVE and copy your new link from Console → Settings";
    }
    if (!token) {
        return "Missing OBS token — copy your OBS link from the SPCLIVE console";
    }
    if (status === "invalid") {
        return "Invalid OBS link — regenerate your OBS token in Settings and update this Browser Source";
    }
    return null;
}

// Unmute so mic passthrough is audible: OBS Browser Source allows unmuted
// autoplay; regular browsers fall back to muted until the user clicks.
function useObsUnmute(status, remoteVideoRef) {
    useEffect(() => {
        if (status !== "live") return undefined;
        const el = remoteVideoRef.current;
        if (!el) return undefined;
        const tryUnmute = () => {
            el.muted = false;
            el.play().catch(() => {
                el.muted = true;
                el.play().catch(() => {});
            });
        };
        tryUnmute();
        window.addEventListener("click", tryUnmute);
        return () => window.removeEventListener("click", tryUnmute);
    }, [status, remoteVideoRef]);
}

const ErrorOverlay = ({ message }) => (
    <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black px-6"
        data-testid="obs-status-overlay"
    >
        <CornerFrame />
        <div className="font-display font-bold uppercase text-3xl text-white/20 tracking-tight">
            SPCLIVE
        </div>
        <AlertTriangle className="w-6 h-6 text-blaze" />
        <div
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 text-center leading-relaxed max-w-xl"
            data-testid="obs-status-detail"
        >
            {message}
        </div>
    </div>
);

const WaitingOverlay = ({ status }) => (
    <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-black px-6"
        data-testid="obs-status-overlay"
    >
        <CornerFrame />
        <div className="font-display font-bold uppercase text-5xl sm:text-6xl text-white/10 tracking-tight">
            Awaiting Signal
        </div>
        <div className="flex items-center gap-3">
            {status === "connecting" && <Loader2 className="w-4 h-4 text-white/30 animate-spin" />}
            <div
                className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/35"
                data-testid="obs-status-detail"
            >
                {status === "connecting"
                    ? "Connecting to SPCLIVE..."
                    : "S P C L I V E — open your Studio to go live"}
            </div>
        </div>
    </div>
);

export default function ObsOutput() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const isLegacyLink = !token && !!searchParams.get("channel");

    const { remoteVideoRef, status } = useMirrorViewer({ obsToken: token });
    useObsUnmute(status, remoteVideoRef);

    const message = obsMessage(token, isLegacyLink, status);
    const showError = Boolean(message);
    const showWaiting = !showError && status !== "live";

    return (
        <div
            className="fixed inset-0 bg-black overflow-hidden cursor-none"
            data-testid="obs-output-page"
        >
            <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="obs-remote-video"
            />
            {showError && <ErrorOverlay message={message} />}
            {showWaiting && <WaitingOverlay status={status} />}
        </div>
    );
}
